import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { AgencyWage, AgencyWageBulkUpsertRequest, AgencyWageBulkUpsertResponse } from "../../../utils/models";
import axiosInstance from "../../../utils/networkWrapper";

type SaveContext = { previousWages?: AgencyWage[]; requestId: number };

/** Keyed by department, so every reader of a department's agency rates shares one cache entry. */
export const agencyWagesQueryKey = (departmentId: string) => ["agency-wages", departmentId] as const;

/**
 * A department's agency rates as stored — the read behind Team → Agency Configuration, shared with the
 * wage breakdown's preview of an agency's row (same key, so either warms the other's cache). No caching
 * policy of its own: a caller that wants one spreads it in.
 */
export const agencyWagesQueryOptions = (departmentId: string) =>
	queryOptions({
		queryKey: agencyWagesQueryKey(departmentId),
		queryFn: async () => {
			const response = await axiosInstance.get<AgencyWage[]>(`/agency-wages/${departmentId}`);
			return response.data;
		},
		enabled: !!departmentId,
	});

/** The Agency Configuration grid's read, with the app's query defaults. */
export const useAgencyWagesQuery = (departmentId: string) => useQuery(agencyWagesQueryOptions(departmentId));

export const useAgencyWages = () => {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const queryKey = agencyWagesQueryKey(chosenDepartment.department_id);

	// Saves are not serialized — a cell blur can overlap an in-flight row save. Each attempt takes a
	// ticket so a slow older response can't clobber the cache with data that predates a newer save.
	const saveTicket = useRef(0);
	const latestSaveTicket = useRef(0);

	const { data: agencyWages = [], isLoading } = useAgencyWagesQuery(chosenDepartment.department_id);

	// One mutation for the whole grid. Previously create/update/batch-update each owned a snackbar,
	// so a single Save could emit a success and a failure at the same time (DAT-577).
	const bulkSaveMutation = useMutation<AgencyWageBulkUpsertResponse, Error, AgencyWageBulkUpsertRequest, SaveContext>({
		mutationFn: async (request) => {
			const response = await backendService.bulkUpsertAgencyWages(request);
			return response.data;
		},
		onMutate: async (request) => {
			saveTicket.current += 1;
			const requestId = saveTicket.current;
			latestSaveTicket.current = requestId;

			track("agency_wage_save_start", {
				department_id: request.departmentId,
				agency_name: request.agencyName,
				role_count: request.entries.length,
			});

			await queryClient.cancelQueries({ queryKey });
			const previousWages = queryClient.getQueryData<AgencyWage[]>(queryKey);

			queryClient.setQueryData<AgencyWage[]>(queryKey, (old = []) => {
				const optimistic = [...old];
				for (const entry of request.entries) {
					const index = optimistic.findIndex((wage) => wage.agencyName === request.agencyName && wage.role === entry.role);
					if (index >= 0) {
						optimistic[index] = { ...optimistic[index], wage: entry.wage };
					} else {
						optimistic.push({ departmentId: request.departmentId, agencyName: request.agencyName, role: entry.role, wage: entry.wage });
					}
				}
				return optimistic;
			});

			return { previousWages, requestId };
		},
		onError: (error, request, context) => {
			// Only the newest attempt may roll the cache back; an older failure would otherwise restore a
			// snapshot that predates a newer successful save.
			if (context?.previousWages && context.requestId === latestSaveTicket.current) {
				queryClient.setQueryData(queryKey, context.previousWages);
			}
			trackError("agency_wage_save_error", {
				department_id: request.departmentId,
				agency_name: request.agencyName,
				error: error.message,
			});
			showErrorSnackbar(t("agency.wages.update.failed"), {}, error);
		},
		onSuccess: (data, request, context) => {
			track("agency_wage_save_success", {
				department_id: request.departmentId,
				agency_name: request.agencyName,
				role_count: request.entries.length,
				updated_nurse_count: data.updatedNurseCount,
			});

			// The response carries the agency's full post-save row set with real ids, so replacing the
			// slice wholesale is what stops the UI re-creating rows it thinks are missing. Skip it for a
			// stale response — the settle invalidation below still reconciles it.
			if (context.requestId !== latestSaveTicket.current) {
				return;
			}
			queryClient.setQueryData<AgencyWage[]>(queryKey, (old = []) => [...old.filter((wage) => wage.agencyName !== request.agencyName), ...data.wages]);
			queryClient.invalidateQueries({ queryKey: ["nurses"] });
			showSnackbar(t("agency.wage.update.success"), "success");
		},
		// Reconcile with the server either way: on failure the optimistic rollback alone left the grid
		// showing pre-save values with no refetch, which is why the change never appeared in real time.
		// Only the last save in flight refetches, so overlapping saves don't stampede.
		onSettled: (_data, _error, _request, context) => {
			if (context?.requestId === latestSaveTicket.current) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});

	const deleteMutation = useMutation<void, AxiosError<{ detail: string }>, string, { previousWages?: AgencyWage[]; deleted?: AgencyWage }>({
		mutationFn: async (agencyWageId: string) => {
			await axiosInstance.delete(`/agency-wages/${agencyWageId}`);
		},
		onMutate: async (deletedId) => {
			await queryClient.cancelQueries({ queryKey });
			const previousWages = queryClient.getQueryData<AgencyWage[]>(queryKey);
			const deleted = previousWages?.find((wage) => wage.id === deletedId);

			track("agency_wage_delete_start", {
				department_id: chosenDepartment.department_id,
				agency_name: deleted?.agencyName ?? null,
				role: deleted?.role ?? null,
			});

			queryClient.setQueryData<AgencyWage[]>(queryKey, (old = []) => old.filter((wage) => wage.id !== deletedId));

			return { previousWages, deleted };
		},
		onError: (err, _deletedId, context) => {
			if (context?.previousWages) {
				queryClient.setQueryData(queryKey, context.previousWages);
			}
			trackError("agency_wage_delete_error", {
				department_id: chosenDepartment.department_id,
				agency_name: context?.deleted?.agencyName ?? null,
				error: err.message,
			});
			showErrorSnackbar(t("agency.wage.delete.failed"), {}, err);
		},
		onSuccess: (_data, _deletedId, context) => {
			track("agency_wage_delete_success", {
				department_id: chosenDepartment.department_id,
				agency_name: context?.deleted?.agencyName ?? null,
			});
		},
		// A delete that lands while a save is in flight would otherwise leave the deleted row restored by
		// the save's refetch of pre-delete data.
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	return {
		agencyWages,
		isLoading,
		isSaving: bulkSaveMutation.isPending,
		saveAgencyWages: bulkSaveMutation.mutateAsync,
		deleteAgencyWage: deleteMutation.mutateAsync,
	};
};
